'use client';

import { Star, X, MapPin, Wifi, Waves, UtensilsCrossed, Dumbbell, Beer, Palmtree, Landmark, ArrowRight } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { Card } from '@/shared/ui';
import { Badge } from '@/shared/ui';
import type { CompareHotel } from '../types/hotels.types';
import { useTranslation } from '@/core/i18n';

/* -------------------------------------------------------------------------- */
/*  Props                                                                      */
/* -------------------------------------------------------------------------- */

interface HotelCompareProps {
  hotels: CompareHotel[];
  onRemove?: (hotelId: string) => void;
}

/* -------------------------------------------------------------------------- */
/*  Deterministic INR formatter                                               */
/* -------------------------------------------------------------------------- */

/** A rupee amount, or "Price on request" when there is none (D-26/D-27). */
function formatINR(n: number | null | undefined): string {
  // A rupee amount, or an honest gap. Formatting null yields "0" and undefined
  // yields "NaN", so a property with no rates rendered as Rs 0 — read as free,
  // not as "not yet priced" (D-26/D-27).
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) {
    return 'Price on request';
  }
  const s = String(Math.round(n));
  if (s.length <= 3) return '₹' + s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  return '₹' + rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

function formatNumber(n: number): string {
  const s = n.toString();
  if (s.length <= 3) return s;
  const last3 = s.slice(-3);
  const rest = s.slice(0, -3);
  return rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + last3;
}

/* -------------------------------------------------------------------------- */
/*  Star rating display                                                       */
/* -------------------------------------------------------------------------- */

function StarRating({ count, id }: { count: number; id?: string }) {
  return (
    <span id={id} className="inline-flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={cn(
            'h-3.5 w-3.5',
            i < count ? 'fill-hms-gold text-hms-gold' : 'text-hms-textDim',
          )}
        />
      ))}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*  Amenity icon helper                                                       */
/* -------------------------------------------------------------------------- */

function AmenityIcon({ name }: { name: string }) {
  const cls = 'h-3 w-3 shrink-0';
  switch (name) {
    case 'Wi-Fi': return <Wifi className={cls} />;
    case 'Restaurant': return <UtensilsCrossed className={cls} />;
    case 'Pool': return <Waves className={cls} />;
    case 'Gym': return <Dumbbell className={cls} />;
    case 'Bar': return <Beer className={cls} />;
    case 'Spa': return <Palmtree className={cls} />;
    case 'Beach': return <Palmtree className={cls} />;
    case 'Heritage Tour': return <Landmark className={cls} />;
    default: return null;
  }
}

/* -------------------------------------------------------------------------- */
/*  Rating bar                                                                */
/* -------------------------------------------------------------------------- */

function RatingBar({ rating, id }: { rating: number; id?: string }) {
  const pct = (rating / 5) * 100;
  const color = rating >= 4.7 ? 'bg-hms-green' : rating >= 4.4 ? 'bg-hms-gold' : 'bg-hms-amber';
  return (
    <div id={id} className="flex items-center gap-2">
      <span className="text-sm font-bold text-hms-text">{rating}</span>
      <div className="h-1.5 flex-1 rounded-full bg-white/5">
        <div className={cn('h-full rounded-full', color)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Comparison field rows                                                     */
/* -------------------------------------------------------------------------- */

interface FieldRowProps {
  label: string;
  fieldKey: string;
  hotels: CompareHotel[];
  renderCell: (hotel: CompareHotel) => React.ReactNode;
}

function FieldRow({ label, fieldKey, hotels, renderCell }: FieldRowProps) {
  return (
    <tr className="border-b border-hms-border/50 last:border-b-0">
      <td className="whitespace-nowrap py-3 pr-4 text-[11px] font-semibold uppercase tracking-wider text-hms-textDim">
        {label}
      </td>
      {hotels.map((hotel) => (
        <td
          key={hotel.id}
          id={`hotels-compare-${fieldKey}-${hotel.id}`}
          className="px-3 py-3 text-xs text-hms-text"
        >
          {renderCell(hotel)}
        </td>
      ))}
      {/* Empty placeholder columns */}
      {Array.from({ length: 3 - hotels.length }).map((_, i) => (
        <td key={`empty-${i}`} className="px-3 py-3" />
      ))}
    </tr>
  );
}

/* -------------------------------------------------------------------------- */
/*  Empty State                                                               */
/* -------------------------------------------------------------------------- */

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div
      id="hotels-compare-empty"
      className="flex flex-col items-center justify-center py-16 text-center"
    >
      <svg className="mb-4 h-16 w-16 text-hms-textDim" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="2" y="3" width="8" height="18" rx="1" />
        <rect x="14" y="3" width="8" height="18" rx="1" />
        <path d="M6 8h0M6 12h0M6 16h0M18 8h0M18 12h0M18 16h0" strokeWidth="2" strokeLinecap="round" />
      </svg>
      <p className="text-sm font-semibold text-hms-text">{t('hotelSearch.noHotelsToCompare')}</p>
      <p className="mt-1 max-w-xs text-xs text-hms-textSub">
        {t('hotelSearch.noHotelsToCompareDesc')}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  HotelCompare                                                              */
/* -------------------------------------------------------------------------- */

export function HotelCompare({ hotels: propHotels, onRemove }: HotelCompareProps) {
  const { t } = useTranslation();
  const hotels = propHotels;
  const displayHotels = hotels.slice(0, 3);

  if (propHotels.length === 0 && !onRemove) {
    return (
      <Card id="hotels-compare-container" padding="lg">
        <EmptyState />
      </Card>
    );
  }

  if (displayHotels.length === 0) {
    return (
      <Card id="hotels-compare-container" padding="lg">
        <EmptyState />
      </Card>
    );
  }

  /* Best value highlighting */
  // Only PRICED hotels can hold the "lowest price" badge.
  //
  // `Math.min` coerces null to 0, so an unpriced hotel became the cheapest and
  // won the badge — and with every hotel unpriced the result was 0, badging
  // them all. Infinity when nothing is priced means no hotel matches.
  const pricedValues = displayHotels
    .map((h) => h.price)
    .filter((v): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0);
  const lowestPrice = pricedValues.length > 0 ? Math.min(...pricedValues) : Infinity;
  const highestRating = Math.max(...displayHotels.map((h) => h.rating));
  const mostReviews = Math.max(...displayHotels.map((h) => h.reviews));

  return (
    <Card id="hotels-compare-container" padding="none" className="overflow-hidden">
      {/* Mobile scroll hint */}
      <div id="hotels-compare-scroll-hint" className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-medium text-hms-teal bg-hms-teal/5 border-b border-hms-border/50 sm:hidden">
        <ArrowRight className="w-3 h-3 animate-pulse" />
        {t('hotelSearch.swipeToCompare')}
      </div>
      <div className="overflow-x-auto">
        <table id="hotels-compare-table" className="w-full min-w-[600px]">
          {/* ---- Header: Hotel columns ---- */}
          <thead>
            <tr className="border-b border-hms-border">
              <th className="w-24 sm:w-32 px-3 sm:px-4 py-4" />
              {displayHotels.map((hotel) => (
                <th
                  key={hotel.id}
                  id={`hotels-compare-header-${hotel.id}`}
                  className="px-3 py-4 text-left"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-xs font-semibold text-hms-text">{hotel.name}</p>
                      <div className="mt-0.5 flex items-center gap-1 text-[10px] text-hms-textSub">
                        <MapPin className="h-3 w-3 text-hms-teal" />
                        {hotel.city}
                      </div>
                    </div>
                    {onRemove && (
                      <button
                        id={`hotels-compare-remove-${hotel.id}`}
                        type="button"
                        onClick={() => onRemove(hotel.id)}
                        className="rounded p-1 text-hms-textDim transition-colors hover:bg-white/5 hover:text-hms-red"
                        aria-label={`Remove ${hotel.name} from comparison`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </th>
              ))}
              {Array.from({ length: 3 - displayHotels.length }).map((_, i) => (
                <th key={`empty-${i}`} className="px-3 py-4" />
              ))}
            </tr>
          </thead>

          {/* ---- Body: Comparison rows ---- */}
          <tbody className="divide-y divide-hms-border/30">
            <FieldRow label={t('hotelSearch.starRating')} fieldKey="stars" hotels={displayHotels}
              renderCell={(h) => <StarRating count={h.stars} />} />
            <FieldRow label={t('hotelSearch.pricePerNight')} fieldKey="price" hotels={displayHotels}
              renderCell={(h) => (
                <div className="flex items-center gap-2">
                  <span className="font-semibold">{formatINR(h.price)}</span>
                  {h.price === lowestPrice && displayHotels.length > 1 && (
                    <Badge variant="green" className="text-[9px]">{t('hotelSearch.bestValue')}</Badge>
                  )}
                </div>
              )} />
            <FieldRow label={t('hotelSearch.guestRating')} fieldKey="rating" hotels={displayHotels}
              renderCell={(h) => (
                <div className="space-y-1">
                  <RatingBar rating={h.rating} />
                  {h.rating === highestRating && displayHotels.length > 1 && (
                    <Badge variant="gold" className="text-[9px]">{t('hotelSearch.topRated')}</Badge>
                  )}
                </div>
              )} />
            <FieldRow label={t('hotelSearch.reviews')} fieldKey="reviews" hotels={displayHotels}
              renderCell={(h) => (
                <div className="flex items-center gap-2">
                  <span>{formatNumber(h.reviews)}</span>
                  {h.reviews === mostReviews && displayHotels.length > 1 && (
                    <Badge variant="blue" className="text-[9px]">{t('hotelSearch.mostPopular')}</Badge>
                  )}
                </div>
              )} />
            <FieldRow label={t('hotelSearch.amenities')} fieldKey="amenities" hotels={displayHotels}
              renderCell={(h) => (
                <div className="flex flex-wrap gap-1.5">
                  {h.amenities.map((a) => (
                    <span key={a} className="inline-flex items-center gap-1 rounded-md bg-white/5 px-2 py-0.5 text-[10px] font-medium text-hms-textSub">
                      <AmenityIcon name={a} />
                      {a}
                    </span>
                  ))}
                </div>
              )} />
            <FieldRow label={t('hotelSearch.roomTypes')} fieldKey="roomtypes" hotels={displayHotels}
              renderCell={(h) => (
                <div className="flex flex-wrap gap-1.5">
                  {h.roomTypes.map((rt) => (
                    <Badge key={rt} variant="outline" className="text-[10px]">{rt}</Badge>
                  ))}
                </div>
              )} />
          </tbody>

          {/* ---- Footer: Book Now buttons ---- */}
          <tfoot>
            <tr className="border-t border-hms-border">
              <td className="px-4 py-4" />
              {displayHotels.map((hotel) => (
                <td key={hotel.id} className="px-3 py-4">
                  <a
                    id={`hotels-compare-book-${hotel.id}`}
                    href={`/hotels/${hotel.id}`}
                    className={cn(
                      'flex h-9 w-full items-center justify-center rounded-lg',
                      'bg-hms-gold text-xs font-semibold text-black',
                      'transition-colors hover:bg-hms-goldLt',
                    )}
                  >
                    {t('hotelSearch.bookNow')}
                  </a>
                </td>
              ))}
              {Array.from({ length: 3 - displayHotels.length }).map((_, i) => (
                <td key={`empty-${i}`} className="px-3 py-4" />
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </Card>
  );
}
